import { useCallback, useEffect, useState } from 'react'

import { PinKeypadModal } from 'features/Procedure/components/pin-keypad-modal/pin-keypad-modal'

import { INITIAL_LOCKOUT, isLockedOut, nextLockoutState, type LockoutState } from './pin-lockout'
import styles from './pin-prompt-modal.module.scss'

type PinPromptModalProps = {
  isVisible: boolean
  /** Any PIN in the list unlocks the dialog. */
  emrPins: string[]
  pinLength: number
  onCancel?: () => void
  onVerified: () => void
  dismissible?: boolean
  loading?: boolean
  zIndex?: number
}

export const PinPromptModal = ({
  isVisible,
  emrPins,
  pinLength,
  onCancel,
  onVerified,
  dismissible = true,
  loading = false,
  zIndex,
}: PinPromptModalProps) => {
  const [lockout, setLockout] = useState<LockoutState>(INITIAL_LOCKOUT)
  const [error, setError] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const locked = isLockedOut(lockout, now)
  const remainingSeconds = lockout.lockedUntil
    ? Math.max(0, Math.ceil((lockout.lockedUntil - now) / 1000))
    : 0

  useEffect(() => {
    if (!isVisible) {
      setError(false)
    }
  }, [isVisible])

  useEffect(() => {
    if (lockout.lockedUntil === null) return
    const remainingMs = lockout.lockedUntil - Date.now()
    if (remainingMs <= 0) return
    const timerId = setTimeout(() => setNow(Date.now()), remainingMs)
    return () => clearTimeout(timerId)
  }, [lockout.lockedUntil])

  const handleSubmit = useCallback(
    (entered: string): boolean => {
      if (locked) return false
      if (emrPins.includes(entered)) {
        onVerified()
        return true
      }
      setError(true)
      setLockout((prev) => nextLockoutState(prev, Date.now()))
      setNow(Date.now())
      return false
    },
    [emrPins, locked, onVerified],
  )

  const errorText = locked
    ? `Too many attempts. Try again in ${remainingSeconds}s.`
    : error
    ? 'Incorrect PIN. Please try again.'
    : null

  return (
    <PinKeypadModal
      isVisible={isVisible}
      pinLength={pinLength}
      onSubmit={handleSubmit}
      onClose={onCancel}
      title='Profile PIN'
      subtitle='Enter the profile PIN to close the intake forms.'
      dismissible={dismissible}
      closable
      cancelText='Cancel'
      loading={loading}
      error={errorText}
      disabled={locked}
      zIndex={zIndex}
      width={440}
      modalClassNames={{
        wrapper: styles.wrapper,
        body: styles.body,
        subtitle: styles.hint,
      }}
    />
  )
}
